/**
 * API client factory for the Spark backend.
 *
 * Extracted from packages/frontend/src/services/api.ts so the admin app can
 * talk to the same backend without depending on the frontend package (the
 * frontend must not become a dependency of admin, and vice versa — see the
 * admin extraction entry in ENGINEERING_DECISIONS.md). Behaviourally
 * unchanged: base URL, JSON envelope unwrapping ({ data, meta }), Bearer auth
 * header injection, and single-flight 401 refresh-and-retry.
 *
 * Token storage keys are shared by name ("spark_access_token" etc.) with the
 * frontend app on purpose — the two apps are served from different
 * hostnames/origins, so browser storage never collides between them, and
 * reusing the names costs nothing.
 */

export type RequestOptions = Omit<RequestInit, "body"> & {
  body?: unknown;
  /** Skip automatic auth header injection */
  noAuth?: boolean;
};

interface ApiError {
  message: string;
  code?: string;
  statusCode: number;
}

export class ApiClientError extends Error {
  public statusCode: number;
  public code?: string;

  constructor(error: ApiError) {
    super(
      typeof error.message === "string" ? error.message : "An error occurred"
    );
    this.name = "ApiClientError";
    this.statusCode = error.statusCode;
    this.code = error.code;
  }
}

/**
 * Extract a human-readable error message from any error.
 */
export function getErrorMessage(
  error: unknown,
  fallback = "An unexpected error occurred"
): string {
  if (error instanceof ApiClientError) {
    return error.message;
  }
  if (error instanceof TypeError && error.message === "Failed to fetch") {
    return "Unable to connect to the server. Please check your internet connection.";
  }
  if (error instanceof Error) {
    return error.message;
  }
  if (typeof error === "string") {
    return error;
  }
  return fallback;
}

export function getAccessToken(): string | null {
  return sessionStorage.getItem("spark_access_token");
}

export function setTokens(tokens: {
  accessToken: string;
  idToken?: string;
  refreshToken?: string;
}) {
  sessionStorage.setItem("spark_access_token", tokens.accessToken);
  if (tokens.idToken) {
    sessionStorage.setItem("spark_id_token", tokens.idToken);
  }
  if (tokens.refreshToken) {
    localStorage.setItem("spark_refresh_token", tokens.refreshToken);
  }
}

/** Clear all stored tokens (logout), including the cross-tab refresh token. */
export function clearTokens() {
  clearSessionTokens();
  localStorage.removeItem("spark_refresh_token");
}

/** Clear only this tab's tokens, leaving the shared refresh token alone. */
export function clearSessionTokens() {
  sessionStorage.removeItem("spark_access_token");
  sessionStorage.removeItem("spark_id_token");
}

export interface ApiClient {
  get: <T>(endpoint: string, options?: RequestOptions) => Promise<T>;
  post: <T>(
    endpoint: string,
    body?: unknown,
    options?: RequestOptions
  ) => Promise<T>;
  put: <T>(
    endpoint: string,
    body?: unknown,
    options?: RequestOptions
  ) => Promise<T>;
  patch: <T>(
    endpoint: string,
    body?: unknown,
    options?: RequestOptions
  ) => Promise<T>;
  delete: <T>(endpoint: string, options?: RequestOptions) => Promise<T>;
  /**
   * Public wrapper around the same token-refresh path 401s use internally.
   * For proactive refresh hooks (tab focus / app init) — see
   * useAuthInitialization in the admin app. Returns false when the refresh
   * token itself is expired or missing.
   */
  refresh: () => Promise<boolean>;
}

/**
 * Build an API client bound to `baseUrl`, with a caller-supplied hook for
 * what to do when a session cannot be refreshed (e.g. redirect to the app's
 * own login route — the frontend and admin apps live at different paths, so
 * this cannot be hardcoded here as it was in the pre-extraction version).
 */
export function createApiClient(
  baseUrl: string,
  onSessionExpired: () => void
): ApiClient {
  let refreshPromise: Promise<boolean> | null = null;

  async function attemptTokenRefresh(): Promise<boolean> {
    if (refreshPromise) return refreshPromise;

    refreshPromise = (async () => {
      const refreshToken = localStorage.getItem("spark_refresh_token");
      if (!refreshToken) return false;

      try {
        const response = await fetch(`${baseUrl}/auth/refresh`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ refreshToken }),
        });

        if (!response.ok) return false;

        const json = await response.json();
        const payload = (json.data ?? json) as {
          accessToken?: string;
          idToken?: string;
        };

        if (!payload.accessToken) return false;

        setTokens({
          accessToken: payload.accessToken,
          idToken: payload.idToken,
        });
        return true;
      } catch {
        return false;
      } finally {
        refreshPromise = null;
      }
    })();

    return refreshPromise;
  }

  async function parseError(response: Response): Promise<ApiError> {
    try {
      const data = await response.json();
      if (data.error && typeof data.error === "object") {
        return {
          message: data.error.message || "An error occurred",
          code: data.error.code,
          statusCode: response.status,
        };
      }
      return {
        message:
          typeof data.error === "string"
            ? data.error
            : data.message || "An error occurred",
        code: data.code,
        statusCode: response.status,
      };
    } catch {
      return {
        message: response.statusText || "An error occurred",
        statusCode: response.status,
      };
    }
  }

  async function request<T>(
    endpoint: string,
    options: RequestOptions = {}
  ): Promise<T> {
    const { body, noAuth, headers: customHeaders, ...rest } = options;

    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      ...(customHeaders as Record<string, string>),
    };

    if (!noAuth) {
      const token = getAccessToken();
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }
    }

    const url = `${baseUrl}${endpoint}`;
    const response = await fetch(url, {
      ...rest,
      headers,
      body: body ? JSON.stringify(body) : undefined,
    });

    if (response.status === 401 && !noAuth) {
      const refreshed = await attemptTokenRefresh();
      if (refreshed) {
        headers["Authorization"] = `Bearer ${getAccessToken()}`;
        const retryResponse = await fetch(url, {
          ...rest,
          headers,
          body: body ? JSON.stringify(body) : undefined,
        });

        if (!retryResponse.ok) {
          const error = await parseError(retryResponse);
          throw new ApiClientError(error);
        }
        const retryJson = await retryResponse.json();
        return (retryJson.data !== undefined ? retryJson.data : retryJson) as T;
      }

      clearTokens();
      onSessionExpired();

      throw new ApiClientError({
        message: "Session expired",
        statusCode: 401,
      });
    }

    if (!response.ok) {
      const error = await parseError(response);
      throw new ApiClientError(error);
    }

    if (response.status === 204) {
      return undefined as T;
    }

    const json = await response.json();
    return (json.data !== undefined ? json.data : json) as T;
  }

  return {
    get: <T>(endpoint: string, options?: RequestOptions) =>
      request<T>(endpoint, { ...options, method: "GET" }),
    post: <T>(endpoint: string, body?: unknown, options?: RequestOptions) =>
      request<T>(endpoint, { ...options, method: "POST", body }),
    put: <T>(endpoint: string, body?: unknown, options?: RequestOptions) =>
      request<T>(endpoint, { ...options, method: "PUT", body }),
    patch: <T>(endpoint: string, body?: unknown, options?: RequestOptions) =>
      request<T>(endpoint, { ...options, method: "PATCH", body }),
    delete: <T>(endpoint: string, options?: RequestOptions) =>
      request<T>(endpoint, { ...options, method: "DELETE" }),
    refresh: () => attemptTokenRefresh(),
  };
}
